'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useAuth } from '@/src/context/AuthContext';
import { useWishlist } from '@/src/context/WishlistContext';
import { useLocalizedPath } from '@/src/hooks/useLocalizedPath';
import { FavoriteIcon, FavoriteBorderIcon } from '../icons';
import styles from './StyledComponents.module.css';

interface WishlistToggleProps {
  productId: string;
}

export const WishlistToggle: React.FC<WishlistToggleProps> = ({ productId }) => {
  const t = useTranslations('common.buttons');
  const router = useRouter();
  const { user, isLoading: authLoading } = useAuth();
  const { isInWishlist, isHydrated, add, remove } = useWishlist();
  const { localizeFullPath } = useLocalizedPath();
  const [busy, setBusy] = useState(false);

  const active = !!user && isInWishlist(productId);
  const label = active ? t('removeFromWishlist') : t('addToWishlist');

  const handleClick = async () => {
    if (authLoading || busy) return;
    if (!user) {
      router.push(localizeFullPath('/login'));
      return;
    }
    setBusy(true);
    try {
      if (active) await remove(productId);
      else await add(productId);
    } catch {
      // swallow - optimistic state reverted in context
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || (!!user && !isHydrated);

  return (
    <button
      type="button"
      className={`${styles.wishlistToggleIcon}${active ? ` ${styles.active}` : ''}`}
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={active}
      aria-label={label}
      title={label}
    >
      {active ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
    </button>
  );
};
